import React, { useEffect, useState } from 'react'
import {
	Box,
	Title,
	TextInput,
	Textarea,
	Button,
	Group,
	Table,
	Checkbox,
	Select,
	Text,
	Space,
	Tabs,
	Tooltip,
	ActionIcon,
	Switch,
	Anchor,
} from '@mantine/core'
import Papa from 'papaparse'
import { saveAs } from 'file-saver'
import { getIncompleteCheckInsWithIssuedItems } from '../services/actions/checkInActions'
import { AlertCircle } from 'tabler-icons-react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
	getExperience,
	getItemOptions,
	getOrgLocations,
	getStationOptions,
	getSegmentOptions,
	updateExperience,
	addItemOption,
	addStationOption,
	addSegmentOption,
	deleteStationOption,
	deleteSegmentOption,
	deleteItemOption,
	updateItemOption,
	updateStationOption,
	updateSegmentOption,
} from '../services/actions/experienceActions'
import ItemOptionModal from '../components/ItemOptionModal'
import StationOptionModal from '../components/StationOptionModal'
import SegmentOptionModal from '../components/SegmentOptionModal'
import { setExperienceDetails } from '../services/store/experienceSlice'
import {
	addDefaultBookingLinkProtocol,
	BOOKING_NUMBER_LINK_MAX_LENGTH,
	getBookingNumberLinkError,
} from '../services/utils/bookingNumberLink'

const EXPERIENCE_NAME_MAX_LENGTH = 55
const SIGNATURE_DISCLAIMER_MAX_LENGTH = 550
const INCOMPLETE_CHECK_INS_CSV_FIELDS = [
	'id', 'bookingNumber', 'travelerName', 'travelerEmail', 'travelerPhone',
	'accountName', 'scheduledCheckInDate', 'stationName', 'segmentName',
	'issuedItemCount', 'issuedItems', 'issuingStaff', 'checkInStaff',
]
const COLLECT_FIELD_DEFINITIONS = [
	{ key: 'travelerPhone', label: 'Phone' },
	{ key: 'accountName', label: 'Account Name' },
]

const createDefaultCollectFields = () => {
	const defaults = {}
	COLLECT_FIELD_DEFINITIONS.forEach(({ key }) => {
		defaults[key] = { collect: false, require: false }
	})
	return defaults
}

const normalizeCollectFieldEntry = (value) => {
	const isRecord = value && typeof value === 'object' && !Array.isArray(value)
	const require = isRecord ? Boolean(value.require) : false
	const collect = isRecord ? Boolean(value.collect) || require : require
	return { collect, require }
}

const normalizeCollectFieldsConfig = (value, fallbackValue = null) => {
	const source =
		value && typeof value === 'object' && !Array.isArray(value) ? value : {}
	const fallback =
		fallbackValue &&
		typeof fallbackValue === 'object' &&
		!Array.isArray(fallbackValue)
			? fallbackValue
			: {}

	const normalized = {}
	COLLECT_FIELD_DEFINITIONS.forEach(({ key }) => {
		const effectiveValue =
			source[key] !== undefined ? source[key] : fallback[key]
		normalized[key] = normalizeCollectFieldEntry(effectiveValue)
	})
	return normalized
}

const getInventory = (itemOption) =>
	itemOption?.inventory &&
	typeof itemOption.inventory === 'object' &&
	!Array.isArray(itemOption.inventory)
		? itemOption.inventory
		: {}

const getTotalUnitLimit = (itemOption) => {
	const rawLimit =
		getInventory(itemOption).totalUnitLimit ?? itemOption?.totalUnitLimit
	if (rawLimit === null || rawLimit === undefined || rawLimit === '')
		return null

	const limit = Number(rawLimit)
	return Number.isInteger(limit) && limit >= 0 ? limit : null
}

const getInventoryCount = (itemOption, key) => {
	const count = Number(getInventory(itemOption)[key] || 0)
	return Number.isFinite(count) ? count : 0
}

const formatUnitCount = (value) =>
	Number(value || 0).toLocaleString(undefined, {
		maximumFractionDigits: 0,
	})

const formatCapacityLimit = (itemOption) => {
	const limit = getTotalUnitLimit(itemOption)
	return limit === null ? 'Unlimited' : `${formatUnitCount(limit)} unit limit`
}

const formatAvailableToAllocate = (itemOption) => {
	const limit = getTotalUnitLimit(itemOption)
	if (limit === null) return 'Unlimited'

	const inventory = getInventory(itemOption)
	const available =
		inventory.availableToAllocate !== null &&
		inventory.availableToAllocate !== undefined
			? Number(inventory.availableToAllocate)
			: limit - getInventoryCount(itemOption, 'allocatedUnits')
	return formatUnitCount(
		Math.max(0, Number.isFinite(available) ? available : 0),
	)
}

const EditExperienceDetailsPage = () => {
	const { orgSlug, experienceSlug } = useParams()
	const dispatch = useDispatch()

	const [name, setName] = useState('')
	const [status, setStatus] = useState('active')
	const [allowStaffAddItems, setAllowStaffAddItems] = useState(false)
	const [allowStaffAddCheckIns, setAllowStaffAddCheckIns] = useState(false)
	const [allowCheckInMassImport, setAllowCheckInMassImport] = useState(false)
	const [collectCheckInSignature, setCollectCheckInSignature] = useState(false)
	const [collectFields, setCollectFields] = useState(
		createDefaultCollectFields(),
	)
	const [signatureDisclaimerGroup, setSignatureDisclaimerGroup] = useState('')
	const [signatureDisclaimerIndividual, setSignatureDisclaimerIndividual] =
		useState('')
	const [itemOptions, setItemOptions] = useState([])
	const [stationOptions, setStationOptions] = useState([])
	const [segmentOptions, setSegmentOptions] = useState([])
	const [editingStation, setEditingStation] = useState('')
	const [editingSegment, setEditingSegment] = useState(null)
	const [editingItem, setEditingItem] = useState(null)
	const [shortName, setShortName] = useState('')
	const [timezone, setTimezone] = useState('America/Los_Angeles')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState('')
	const [locationId, setLocationId] = useState(null)
	const [useExperiencePage, setUseExperiencePage] = useState(true)
	const [bookingNumberLink, setBookingNumberLink] = useState('')
	const [locations, setLocations] = useState([])
	const [showItemModal, setShowItemModal] = useState(false)
	const [showStationModal, setShowStationModal] = useState(false)
	const [showSegmentModal, setShowSegmentModal] = useState(false)
	const [saveStatus, setSaveStatus] = useState('idle') // 'idle' | 'saving' | 'saved' | 'error'
	const [lastSaved, setLastSaved] = useState(null)
	const [downloadingIncompleteCheckIns, setDownloadingIncompleteCheckIns] = useState(false)
	const [downloadError, setDownloadError] = useState('')

	const downloadIncompleteCheckIns = async () => {
		if (downloadingIncompleteCheckIns) return
		setDownloadingIncompleteCheckIns(true)
		setDownloadError('')
		try {
			const rows = await getIncompleteCheckInsWithIssuedItems(experienceSlug, orgSlug)
			const csv = Papa.unparse(
				{ fields: INCOMPLETE_CHECK_INS_CSV_FIELDS, data: rows },
				{ escapeFormulae: true },
			)
			saveAs(
				new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
				`${experienceSlug}_incomplete_checkins_with_issued_items.csv`,
			)
		} catch (err) {
			setDownloadError(err.message || 'Failed to download incomplete check-ins')
		} finally {
			setDownloadingIncompleteCheckIns(false)
		}
	}

	useEffect(() => {
		const fetchExperience = async () => {
			const exp = await getExperience(experienceSlug, orgSlug)
			setName(exp.name)
			setStatus(exp.status ?? 'active')
			setAllowStaffAddItems(exp.allowStaffAddItems || false)
			setAllowStaffAddCheckIns(exp.allowStaffAddCheckIns || false)
			setAllowCheckInMassImport(exp.allowCheckInMassImport || false)
			setCollectCheckInSignature(exp.collectCheckInSignature || false)
			setCollectFields(normalizeCollectFieldsConfig(exp.collectFields))
			setSignatureDisclaimerGroup(exp.signatureDisclaimerGroup || '')
			setSignatureDisclaimerIndividual(exp.signatureDisclaimerIndividual || '')
			setShortName(exp.shortName || '')
			setTimezone(exp.timezone || 'America/Los_Angeles')
			setStartDate(exp.startDate ? String(exp.startDate).slice(0, 10) : '')
			setEndDate(exp.endDate ? String(exp.endDate).slice(0, 10) : '')
			setLocationId(exp.locationId || null)
			setUseExperiencePage(exp.useExperiencePage !== false)
			setBookingNumberLink(exp.bookingNumberLink || '')

			const [items, stations, segments, orgLocations] = await Promise.all([
				getItemOptions(experienceSlug, orgSlug),
				getStationOptions(experienceSlug, orgSlug),
				getSegmentOptions(experienceSlug, orgSlug),
				getOrgLocations(orgSlug),
			])
			setItemOptions(items)
			setStationOptions(stations)
			setSegmentOptions(segments)
			setLocations(orgLocations)
		}

		fetchExperience()
	}, [experienceSlug, orgSlug])

	useEffect(() => {
		if (!name) return // don’t run on initial load before fetch
		if (getBookingNumberLinkError(bookingNumberLink)) {
			setSaveStatus('error')
			return
		}

		setSaveStatus('saving')
		const timeout = setTimeout(async () => {
			try {
				const updatedExperience = await updateExperience(
					experienceSlug,
					orgSlug,
					{
						name,
						status,
						allowStaffAddItems,
						allowCheckInMassImport,
						allowStaffAddCheckIns,
						collectCheckInSignature,
						collectFields,
						signatureDisclaimerGroup,
						signatureDisclaimerIndividual,
						shortName,
						timezone,
						startDate: startDate || null,
						endDate: endDate || null,
						locationId: locationId || null,
						useExperiencePage,
						bookingNumberLink,
					},
				)
				dispatch(setExperienceDetails(updatedExperience))
				setSaveStatus('saved')
				setLastSaved(new Date())
			} catch (err) {
				console.error(err)
				setSaveStatus('error')
			}
		}, 500) // wait 0.5s after user stops typing

		return () => clearTimeout(timeout)
	}, [
		name,
		status,
		allowStaffAddItems,
		allowStaffAddCheckIns,
		allowCheckInMassImport,
		collectCheckInSignature,
		collectFields,
		signatureDisclaimerGroup,
		signatureDisclaimerIndividual,
		shortName,
		timezone,
		startDate,
		endDate,
		locationId,
		useExperiencePage,
		bookingNumberLink,
		experienceSlug,
		orgSlug,
		dispatch,
	])

	const bookingNumberLinkError = getBookingNumberLinkError(bookingNumberLink)

	const updateCollectField = (fieldKey, nextEntry) => {
		setCollectFields((prev) => {
			const nextConfig = normalizeCollectFieldsConfig(prev)
			const currentEntry = nextConfig[fieldKey] || {
				collect: false,
				require: false,
			}
			const mergedEntry = {
				...currentEntry,
				...nextEntry,
			}
			if (mergedEntry.require) {
				mergedEntry.collect = true
			}
			if (!mergedEntry.collect) {
				mergedEntry.require = false
			}
			nextConfig[fieldKey] = mergedEntry
			return nextConfig
		})
	}

	return (
		<Box px='md' py='md'>
			<Title order={2}>Edit Experience Details</Title>
			<Group spacing='xs' mb='md'>
				{saveStatus === 'saving' && <Text c='blue'>Saving...</Text>}
				{saveStatus === 'saved' && (
					<Text c='dimmed'>
						Saved{' '}
						{lastSaved &&
							`at ${lastSaved.toLocaleTimeString([], {
								hour: 'numeric',
								minute: '2-digit',
							})}`}
					</Text>
				)}
				{saveStatus === 'error' && <Text c='red'>Error saving</Text>}
			</Group>

			<Tabs defaultValue='general' radius='md'>
				<Tabs.List grow>
					<Tabs.Tab value='general'>General</Tabs.Tab>
					<Tabs.Tab value='stations'>Stations</Tabs.Tab>
					<Tabs.Tab value='segments'>Segments</Tabs.Tab>
					<Tabs.Tab value='items'>Items</Tabs.Tab>
					<Tabs.Tab value='advanced'>Advanced</Tabs.Tab>
				</Tabs.List>

				{/* ---- General Settings Tab ---- */}
				<Tabs.Panel value='general' pt='md'>
					<TextInput
						label='Experience Name'
						value={name}
						onChange={(e) =>
							setName(e.target.value.slice(0, EXPERIENCE_NAME_MAX_LENGTH))
						}
						maxLength={EXPERIENCE_NAME_MAX_LENGTH}
						mb='sm'
					/>
					<TextInput
						label={
							<Group spacing={6} align='center' noWrap>
								<Text span>Public Name Override</Text>
								<Tooltip
									withArrow
									multiline
									width={280}
									label='Optional: this is the public-facing name shown on passes, emails, and SMS. Leave blank to automatically use Experience Name.'
								>
									<ActionIcon
										size='sm'
										variant='subtle'
										color='gray'
										aria-label='About Public Name Override'
									>
										<AlertCircle size={14} />
									</ActionIcon>
								</Tooltip>
							</Group>
						}
						value={shortName}
						onChange={(e) => setShortName(e.target.value)}
						mb='sm'
					/>
					<Select
						label='Status'
						value={status}
						onChange={setStatus}
						data={[
							{ value: 'active', label: 'Active' },
							{ value: 'inactive', label: 'Inactive' },
						]}
						mb='md'
					/>
					<Select
						label='Timezone'
						value={timezone}
						onChange={setTimezone}
						data={[
							{ value: 'America/Los_Angeles', label: 'Pacific (PT)' },
							{ value: 'America/Denver', label: 'Mountain (MT)' },
							{ value: 'America/Chicago', label: 'Central (CT)' },
							{ value: 'America/New_York', label: 'Eastern (ET)' },
							{ value: 'America/Santiago', label: 'Chile (Santiago)' },
							{
								value: 'America/Argentina/Buenos_Aires',
								label: 'Argentina (Buenos Aires)',
							},
							{ value: 'Europe/London', label: 'London (UK)' },
							{ value: 'Europe/Lisbon', label: 'Western Europe' },
							{ value: 'Europe/Berlin', label: 'Central Europe' },
							{ value: 'Europe/Athens', label: 'Eastern Europe' },
							{ value: 'Asia/Tokyo', label: 'Japan (JST)' },
						]}
						mb='sm'
					/>
					<TextInput
						label='Start Date'
						type='date'
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						mb='sm'
					/>
					<TextInput
						label='End Date'
						type='date'
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						mb='sm'
					/>
					<Select
						label='Location'
						placeholder='Select a location'
						value={locationId}
						onChange={setLocationId}
						data={locations.map((loc) => ({
							value: loc.id,
							label: loc.name,
						}))}
						clearable
						searchable
						mb='sm'
					/>

					<Space h='md' />

					<Checkbox
						label='Allow staff to add Purchased Items'
						checked={allowStaffAddItems}
						onChange={(e) => setAllowStaffAddItems(e.currentTarget.checked)}
						mb='md'
					/>
					<Checkbox
						label='Allow staff to add new Check-Ins'
						checked={allowStaffAddCheckIns}
						onChange={(e) => setAllowStaffAddCheckIns(e.currentTarget.checked)}
						mb='md'
					/>
					<Checkbox
						label='Allow Check-Ins mass upload'
						checked={allowCheckInMassImport}
						onChange={(e) => setAllowCheckInMassImport(e.currentTarget.checked)}
						mb='md'
					/>
					<Checkbox
						label='Collect check-in signature'
						checked={collectCheckInSignature}
						onChange={(e) =>
							setCollectCheckInSignature(e.currentTarget.checked)
						}
						mb='sm'
					/>
					{collectCheckInSignature && (
						<>
							<Textarea
								label='Group Signature Disclaimer'
								placeholder='Shown before group signature'
								value={signatureDisclaimerGroup}
								onChange={(e) =>
									setSignatureDisclaimerGroup(
										e.target.value.slice(0, SIGNATURE_DISCLAIMER_MAX_LENGTH),
									)
								}
								autosize
								minRows={5}
								maxRows={12}
								maxLength={SIGNATURE_DISCLAIMER_MAX_LENGTH}
								mb='xs'
							/>
							<Textarea
								label='Individual Signature Disclaimer'
								placeholder='Shown before individual signature'
								value={signatureDisclaimerIndividual}
								onChange={(e) =>
									setSignatureDisclaimerIndividual(
										e.target.value.slice(0, SIGNATURE_DISCLAIMER_MAX_LENGTH),
									)
								}
								autosize
								minRows={5}
								maxRows={12}
								maxLength={SIGNATURE_DISCLAIMER_MAX_LENGTH}
								mb='md'
							/>
						</>
					)}
				</Tabs.Panel>

				<Tabs.Panel value='advanced' pt='md'>
					<Title order={4} mb='xs'>
						Pass Link Destination
					</Title>
					<Switch
						label='Use Experience Page'
						description='When enabled, pass links open the traveler Experience Page. When disabled, they open the check-in pass directly.'
						checked={useExperiencePage}
						onChange={(event) =>
							setUseExperiencePage(event.currentTarget.checked)
						}
						mb='xl'
					/>

					<Title order={4} mb='xs'>
						Collect Fields
					</Title>
					<Text size='sm' c='dimmed' mb='md'>
						If these traveler fields are missing during check-in, a prompt to
						fill them out will be shown to the staff.
					</Text>

					<Table withBorder>
						<thead>
							<tr>
								<th>Field</th>
								<th>Collect</th>
								<th>Require</th>
							</tr>
						</thead>
						<tbody>
							{COLLECT_FIELD_DEFINITIONS.map((field) => {
								const fieldConfig = collectFields[field.key] || {
									collect: false,
									require: false,
								}
								return (
									<tr key={field.key}>
										<td>{field.label}</td>
										<td>
											<Checkbox
												aria-label={`Collect ${field.label}`}
												checked={fieldConfig.collect}
												onChange={(event) =>
													updateCollectField(field.key, {
														collect: event.currentTarget.checked,
													})
												}
											/>
										</td>
										<td>
											<Checkbox
												aria-label={`Require ${field.label}`}
												checked={fieldConfig.require}
												disabled={!fieldConfig.collect}
												onChange={(event) =>
													updateCollectField(field.key, {
														require: event.currentTarget.checked,
													})
												}
											/>
										</td>
									</tr>
								)
							})}
						</tbody>
					</Table>

					<Title order={4} mt='xl' mb='xs'>
						Other Advanced Settings
					</Title>
					<TextInput
						label='Booking Number Link'
						description='Use {{bookingNumber}} where the check-in booking number belongs within the url.'
						placeholder='www.example.com/bookings/{{bookingNumber}}/mybooking'
						value={bookingNumberLink}
						maxLength={BOOKING_NUMBER_LINK_MAX_LENGTH}
						onChange={(event) => setBookingNumberLink(event.target.value)}
						onBlur={() => {
							if (bookingNumberLink && !bookingNumberLinkError) {
								setBookingNumberLink(
									addDefaultBookingLinkProtocol(bookingNumberLink),
								)
							}
						}}
						error={bookingNumberLinkError || undefined}
					/>
					<Anchor
						component='button'
						type='button'
						onClick={downloadIncompleteCheckIns}
						disabled={downloadingIncompleteCheckIns}
						mt='md'
					>
						{downloadingIncompleteCheckIns
							? 'Downloading…'
							: 'Download incomplete check-ins with issued items'}
					</Anchor>
					{downloadError && <Text role='alert' color='red' mb='sm'>{downloadError}</Text>}
				</Tabs.Panel>

				{/* ---- Stations Tab ---- */}
				<Tabs.Panel value='stations' pt='md'>
					<Title order={4} mb='sm'>
						Stations
					</Title>
					<Table withBorder>
						<tbody>
							{stationOptions
								.slice()
								.sort((a, b) => a.name.localeCompare(b.name))
								.map((station) => (
									<tr key={station.id}>
										<td>
											<Text>{station.name}</Text>
											{station.address && (
												<Text size='xs' c='dimmed'>
													{station.address}
												</Text>
											)}
										</td>
										<td style={{ textAlign: 'right' }}>
											<Group spacing='xs' position='right'>
												<Button
													variant='outline'
													size='xs'
													onClick={() => {
														setEditingStation(station)
														setShowStationModal(true)
													}}
												>
													Edit
												</Button>
												<Button
													variant='outline'
													color='red'
													size='xs'
													onClick={async () => {
														if (window.confirm('Delete this station?')) {
															await deleteStationOption(
																experienceSlug,
																orgSlug,
																station.id,
															)
															setStationOptions((prev) =>
																prev.filter((i) => i.id !== station.id),
															)
														}
													}}
												>
													Delete
												</Button>
											</Group>
										</td>
									</tr>
								))}
						</tbody>
					</Table>

					<Button mt='sm' onClick={() => setShowStationModal(true)}>
						➕ Add Station
					</Button>
				</Tabs.Panel>

				{/* ---- Segments Tab ---- */}
				<Tabs.Panel value='segments' pt='md'>
					<Title order={4} mb='sm'>
						Segments
					</Title>
					<Table withBorder>
						<tbody>
							{segmentOptions.length === 0 ? (
								<tr>
									<td>
										<Text size='sm' c='dimmed'>
											No segment options yet.
										</Text>
									</td>
								</tr>
							) : (
								segmentOptions
									.slice()
									.sort((a, b) => a.name.localeCompare(b.name))
									.map((segment) => (
										<tr key={segment.id}>
											<td>{segment.name}</td>
											<td style={{ textAlign: 'right' }}>
												<Group spacing='xs' position='right'>
													<Button
														variant='outline'
														size='xs'
														onClick={() => {
															setEditingSegment(segment)
															setShowSegmentModal(true)
														}}
													>
														Edit
													</Button>
													<Button
														variant='outline'
														color='red'
														size='xs'
														onClick={async () => {
															if (window.confirm('Delete this segment?')) {
																await deleteSegmentOption(
																	experienceSlug,
																	orgSlug,
																	segment.id,
																)
																setSegmentOptions((prev) =>
																	prev.filter((item) => item.id !== segment.id),
																)
															}
														}}
													>
														Delete
													</Button>
												</Group>
											</td>
										</tr>
									))
							)}
						</tbody>
					</Table>

					<Button mt='sm' onClick={() => setShowSegmentModal(true)}>
						➕ Add Segment
					</Button>
				</Tabs.Panel>

				{/* ---- Items Tab ---- */}
				<Tabs.Panel value='items' pt='md'>
					<Title order={4} mb='sm'>
						Item Options
					</Title>
					<Table withBorder>
						<thead>
							<tr>
								<th>Item</th>
								<th>Store</th>
								<th>Capacity</th>
								<th>Allocated</th>
								<th>Available to Allocate</th>
								<th>Picked Up</th>
								<th>Left to Pick Up</th>
								<th style={{ textAlign: 'right' }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{itemOptions.length === 0 ? (
								<tr>
									<td colSpan={8}>
										<Text size='sm' c='dimmed'>
											No item options yet.
										</Text>
									</td>
								</tr>
							) : (
								itemOptions
									.slice()
									.sort((a, b) => a.name.localeCompare(b.name))
									.map((opt) => (
										<tr key={opt.id}>
											<td>{opt.name}</td>
											<td>{opt.showInExperienceStore ? 'Shown' : 'Hidden'}</td>
											<td>{formatCapacityLimit(opt)}</td>
											<td>
												{formatUnitCount(
													getInventoryCount(opt, 'allocatedUnits'),
												)}
											</td>
											<td>{formatAvailableToAllocate(opt)}</td>
											<td>
												{formatUnitCount(
													getInventoryCount(opt, 'pickedUpUnits'),
												)}
											</td>
											<td>
												{formatUnitCount(
													getInventoryCount(opt, 'leftToPickUpUnits'),
												)}
											</td>
											<td style={{ textAlign: 'right' }}>
												<Group spacing='xs' position='right'>
													<Button
														variant='outline'
														size='xs'
														onClick={() => {
															setEditingItem(opt)
															setShowItemModal(true)
														}}
													>
														Edit
													</Button>
													<Button
														variant='outline'
														color='red'
														size='xs'
														onClick={async () => {
															if (window.confirm('Delete this item?')) {
																await deleteItemOption(
																	experienceSlug,
																	orgSlug,
																	opt.id,
																)
																setItemOptions((prev) =>
																	prev.filter((i) => i.id !== opt.id),
																)
															}
														}}
													>
														Delete
													</Button>
												</Group>
											</td>
										</tr>
									))
							)}
						</tbody>
					</Table>

					<Button mt='sm' onClick={() => setShowItemModal(true)}>
						➕ Add Item Option
					</Button>
				</Tabs.Panel>
			</Tabs>

			<ItemOptionModal
				show={showItemModal}
				onClose={() => {
					setShowItemModal(false)
					setEditingItem(null)
				}}
				item={editingItem}
				onAdd={async (newItem) => {
					if (editingItem) {
						const updated = await updateItemOption(
							experienceSlug,
							orgSlug,
							editingItem.id,
							newItem,
						)
						setItemOptions((prev) =>
							prev.map((item) => (item.id === updated.id ? updated : item)),
						)
					} else {
						const added = await addItemOption(experienceSlug, orgSlug, newItem)
						setItemOptions((prev) => [...prev, added])
					}
					setEditingItem(null)
					setShowItemModal(false)
				}}
			/>
			<StationOptionModal
				show={showStationModal}
				onClose={() => {
					setShowStationModal(false)
					setEditingStation(null)
				}}
				experienceSlug={experienceSlug}
				station={editingStation}
				onAdd={async (newStation) => {
					if (editingStation) {
						const updated = await updateStationOption(
							experienceSlug,
							orgSlug,
							editingStation.id,
							newStation,
						)
						setStationOptions((prev) =>
							prev.map((station) =>
								station.id === updated.id ? updated : station,
							),
						)
					} else {
						const added = await addStationOption(
							experienceSlug,
							orgSlug,
							newStation,
						)
						setStationOptions((prev) => [...prev, added])
					}
					setEditingStation(null)
					setShowStationModal(false)
				}}
			/>
			<SegmentOptionModal
				show={showSegmentModal}
				onClose={() => {
					setShowSegmentModal(false)
					setEditingSegment(null)
				}}
				segment={editingSegment}
				onAdd={async (newSegment) => {
					if (editingSegment) {
						const updated = await updateSegmentOption(
							experienceSlug,
							orgSlug,
							editingSegment.id,
							newSegment,
						)
						setSegmentOptions((prev) =>
							prev.map((segment) =>
								segment.id === updated.id ? updated : segment,
							),
						)
					} else {
						const added = await addSegmentOption(
							experienceSlug,
							orgSlug,
							newSegment,
						)
						setSegmentOptions((prev) => [...prev, added])
					}
					setEditingSegment(null)
					setShowSegmentModal(false)
				}}
			/>
		</Box>
	)
}

export default EditExperienceDetailsPage
